import { Logo } from '@components/layout/components/Logo'
import { ThemeChangeButton } from '@components/themeChangeButton/ThemeChangeButton'
import { ConfigProviderContext } from '@components/utils/config-provider'
import { useAuthenticator } from '@features/auth/utils/useAuthenticator'
import { getStatusValue } from '@features/indexing/components/utils'
import { indexStatuses, useGetIndexStatus } from '@features/indexing/hooks/useGetIndexStatus'
import { SystemStatus } from '@features/system-status/components/SystemStatus'
import Language from '@mui/icons-material/Language'
import Logout from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import { alertClasses } from '@mui/material'
import Alert from '@mui/material/Alert'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Dispatch, SetStateAction, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  toggleMenu: Dispatch<SetStateAction<boolean>>
  isOpenMenu: boolean
}

export const Header = ({ toggleMenu, isOpenMenu }: Props) => {
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorElement)
  const { t, i18n } = useTranslation()
  const { logout } = useAuthenticator()
  const { data: statusData, isLoading: isLoadingStatus } = useGetIndexStatus()
  const statusValue = getStatusValue(statusData)
  const context = useContext(ConfigProviderContext)
  const headerText = `${t('layout.header.title')} ${context?.config?.companyName || ''}`

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorElement(null)
  }

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language)
    handleClose()
  }

  const ToggleMenuIcon = isOpenMenu ? MenuOpenIcon : MenuIcon

  return (
    <>
      <AppBar
        position="static"
        sx={{
          height: 50,
          background: (theme) =>
            `linear-gradient(to left, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignContent="center"
          alignItems="center"
          height={50}
        >
          <Box ml={3}>
            <IconButton
              onClick={() => {
                toggleMenu((previous) => !previous)
              }}
            >
              <ToggleMenuIcon sx={{ color: (theme) => theme.palette.common.white }} />
            </IconButton>
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignContent="center"
            alignItems="center"
          >
            <SystemStatus />
            <Box>
              <ThemeChangeButton />
            </Box>
            <Box>
              <Tooltip title={t('layout.header.switch_language.text')}>
                <IconButton
                  aria-controls={open ? 'language-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClick}
                >
                  <Language />
                </IconButton>
              </Tooltip>
              <Menu
                id="language-menu"
                anchorEl={anchorElement}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                <MenuItem
                  selected={i18n.language === 'en'}
                  onClick={() => handleLanguageChange('en')}
                >
                  English
                </MenuItem>
                <MenuItem
                  selected={i18n.language === 'pl'}
                  onClick={() => handleLanguageChange('pl')}
                >
                  Polski
                </MenuItem>
                <MenuItem
                  selected={i18n.language === 'de'}
                  onClick={() => handleLanguageChange('de')}
                >
                  Deutsch
                </MenuItem>
              </Menu>
              <Tooltip title={t('layout.header.logout')}>
                <IconButton
                  onClick={() => {
                    logout()
                  }}
                >
                  <Logout />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </AppBar>

      <Box
        sx={{ width: '100%', height: '100%' }}
        pb={1}
        px={4}
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: '1fr auto 1fr' }}
        justifyContent={{ xs: 'center', md: 'start' }}
        alignContent="center"
        alignItems="center"
      >
        <Box>
          <Logo />
        </Box>
        <Box sx={{ px: 4 }}>
          {![indexStatuses.READY, indexStatuses.STOPPED].includes(statusData?.status || indexStatuses.READY) && (
            <Alert
              color="info"
              sx={{
                [`& .${alertClasses.icon}`]: {
                  padding: 0,
                },
              }}
              icon={
                <Box
                  sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', alignContent: 'center' }}
                >
                  <CircularProgress
                    size={32}
                    value={statusValue}
                    variant={isLoadingStatus ? 'indeterminate' : 'determinate'}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {!isLoadingStatus && (
                      <Typography
                        variant="caption"
                        component="div"
                        color="primary"
                        sx={{
                          fontSize: '10px',
                          fontFamily: 'arial',
                          fontWeight: 'bold',
                          transition: '0.3s all',
                          lineHeight: 1,
                        }}
                      >
                        {Math.round(statusValue)}%
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
            >
              {t('index.status.indexing')}
            </Alert>
          )}
        </Box>
        <Box sx={{ width: '100%' }}>
          <Typography
            variant="h2"
            align="right"
            color="primary"
          >
            {headerText}
          </Typography>
        </Box>
      </Box>
    </>
  )
}
